"use client";

import { useTranslations } from "next-intl";
import type * as React from "react";
import { usePriceConverter } from "@/hooks/price/usePriceConverter";
import { formatStakeNumber } from "@/lib/stake/format-number";

interface UvmHeadlineBarProps {
  /** UVM token-holder count (from the server-side indexer). */
  holderCount: number | undefined;
}

/** Yellow headline bar: UVM price (right) + holder count (left). */
export const UvmHeadlineBar: React.FC<UvmHeadlineBarProps> = ({
  holderCount,
}) => {
  const t = useTranslations("stake");
  const { rates } = usePriceConverter();
  const price = rates ? `$${formatStakeNumber(rates.uvmToUsd)}` : "—";

  return (
    // Each stat is centered within its own half (flex-1) with a divider
    // between, so they read as two balanced columns. RTL source order: price
    // first (rightmost), divider, holders (leftmost).
    <div className="flex items-center rounded-lg bg-accent-secondary text-text-button-label px-4 py-3 font-bold">
      <div className="flex flex-1 flex-col items-center gap-1">
        <span className="text-xs">{t("globalStats.uvmPrice")}</span>
        <span className="text-sm font-inter" dir="ltr">
          {price}
        </span>
      </div>
      <div
        className="h-9 w-px shrink-0 bg-text-button-label/30"
        aria-hidden="true"
      />
      <div className="flex flex-1 flex-col items-center gap-1">
        <span className="text-xs">{t("globalStats.uvmHolders")}</span>
        {holderCount != null ? (
          <span className="text-sm">
            <span className="font-inter" dir="ltr">
              {formatStakeNumber(holderCount)}
            </span>{" "}
            {t("globalStats.usersSuffix")}
          </span>
        ) : (
          <span className="text-sm">—</span>
        )}
      </div>
    </div>
  );
};
